import {
  Code,
  Heading1,
  Heading2,
  Heading3,
  List,
  ListOrdered,
  Minus,
  Pilcrow,
  Quote,
  Table,
  type LucideIcon,
} from "lucide-react";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";
import type { SlashBridge, SlashMenuState } from "./slash-command";

const ICONS: Record<string, LucideIcon> = {
  Pilcrow,
  Heading1,
  Heading2,
  Heading3,
  List,
  ListOrdered,
  Table,
  Quote,
  Code,
  Minus,
};

const MENU_MAX_HEIGHT = 288;

interface SlashMenuProps {
  state: SlashMenuState | null;
  bridge: { current: SlashBridge };
}

/**
 * The "/" menu popup.
 *
 * Keyboard handling lives here and is registered onto the bridge, so the
 * extension can offer arrow keys and Enter to whichever menu is currently
 * mounted without knowing anything about it.
 */
export function SlashMenu({ state, bridge }: SlashMenuProps) {
  const [selected, setSelected] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<(HTMLButtonElement | null)[]>([]);

  // A new query is a new list, so the highlight goes back to the top.
  useEffect(() => setSelected(0), [state?.query]);

  useEffect(() => {
    const items = state?.items ?? [];
    bridge.current.onKeyDown = (event) => {
      if (!items.length) return false;

      if (event.key === "ArrowDown") {
        setSelected((current) => (current + 1) % items.length);
        return true;
      }
      if (event.key === "ArrowUp") {
        setSelected((current) => (current - 1 + items.length) % items.length);
        return true;
      }
      if (event.key === "Enter" || event.key === "Tab") {
        setSelected((current) => {
          state?.select(items[current] ?? items[0]);
          return current;
        });
        return true;
      }
      return false;
    };

    return () => {
      bridge.current.onKeyDown = undefined;
    };
  }, [state, bridge]);

  useLayoutEffect(() => {
    itemRefs.current[selected]?.scrollIntoView({ block: "nearest" });
  }, [selected]);

  if (!state || !state.rect) return null;

  if (!state.items.length) {
    return (
      <div
        className="fixed z-50 rounded-md border border-border bg-popover px-3 py-2 text-xs text-muted-foreground shadow-md"
        style={placement(state.rect)}
      >
        No blocks match "{state.query}"
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      role="listbox"
      aria-label="Insert block"
      className="fixed z-50 w-56 overflow-y-auto rounded-md border border-border bg-popover p-1 shadow-md"
      style={{ ...placement(state.rect), maxHeight: MENU_MAX_HEIGHT }}
    >
      {state.items.map((item, index) => {
        const Icon = ICONS[item.icon] ?? Pilcrow;
        return (
          <button
            key={item.title}
            ref={(element) => {
              itemRefs.current[index] = element;
            }}
            type="button"
            role="option"
            aria-selected={index === selected}
            // Mouse down, not click: click fires after the editor has already
            // lost the selection the command needs.
            onMouseDown={(event) => {
              event.preventDefault();
              state.select(item);
            }}
            onMouseEnter={() => setSelected(index)}
            className={cn(
              "flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm",
              index === selected
                ? "bg-accent text-accent-foreground"
                : "text-foreground"
            )}
          >
            <Icon className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
            {item.title}
          </button>
        );
      })}
    </div>
  );
}

/** Below the caret, unless that would run off the bottom of the window. */
const placement = (rect: NonNullable<SlashMenuState["rect"]>) => {
  const spaceBelow = window.innerHeight - rect.bottom;
  return spaceBelow < MENU_MAX_HEIGHT
    ? { left: rect.left, bottom: window.innerHeight - rect.top + 4 }
    : { left: rect.left, top: rect.bottom + 4 };
};
